import {
  checkForUpdates,
  emptySessionsSection,
  fetchBlankSummarySessions,
  fetchSession,
  fetchSessionsExport,
  fetchSessionsFilters,
  fetchSessionsWithSummary,
  prepareFilter,
  updateSessionsWithSummary,
} from "@actions";
import DownloadButton from "@buttons/DownloadButton";
import OrgTreeButton from "@buttons/OrgTreeButton";
import SummaryButton from "@buttons/SummaryButton";
import { defaultFromDate } from "@helpers/dates";
import { getOptionValue } from "@helpers/options";
import SessionDetailsModal from "@common/components/modals/SessionDetailsModal";
import CompanyFilters from "@shared/CompanyFilters";
import DataGrid from "@shared/DataGrid";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";
import { Grid, Pagination } from "semantic-ui-react";

class SessionsView extends React.Component {
  state = {
    searchParams: {
      startDate: moment(defaultFromDate).toISOString(true),
      endDate: moment().toISOString(true),
      sessionId: null,
      transactionRef: null,
      assetNo: null,
      week: null,
      playerId: null,
      details: {},
      zeroBetSessions: false,
      pageNumber: 1,
    },
    loading: false,
    useCurrentDateTime: true,
    modalOpen: false,
    modalTitle: "",
    showGamePlayEvents: false,
    showSummary: true,
    windowHeight: window.innerHeight,
  };

  handleClose = () => this.setState({ modalOpen: false });

  handleSessionSelection = (term) => {
    this.setState({ sessionId: term.sessionId, modalOpen: true });
  };

  handleResize = () => {
    this.setState({ windowHeight: window.innerHeight });
  };

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.props.emptySessionsSection();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.filters.filterSelected.operator !==
      prevProps.filters.filterSelected.operator
    ) {
      this.props.fetchBlankSummarySessions(
        this.props.filters.filterSelected.operator,
      );
    }

    if (checkForUpdates(prevProps.filters, this.props.filters)) {
      this.handleRefresh();
    }
  }
  setUseCurrentDateTime = () => {
    this.setState({ useCurrentDateTime: !this.state.useCurrentDateTime });
  };

  handleChange = (key, value, callback = () => {}) => {
    let params = this.state.searchParams;
    params[key] = value;
    this.setState({ searchParams: params }, callback);
  };

  /**
   * Find all controls in the grid and populate the details object with their codes and values
   */
  getDetails = () => {
    const details = {};
    const data = this.props.sessionsByCompany.summary.data;

    data.forEach((row, rowIndex) => {
      row.cellData.forEach((cell, cellIndex) => {
        if (cell.type === "control") {
          details[cell.data.code] = getOptionValue(cell.data);
        }
      });
    });

    return details;
  };

  handleOptionChange = (option, value) => {
    const { summary } = this.props.sessionsByCompany;
    const data = [...summary.data];

    data.forEach((row, rowIndex) => {
      row.cellData.forEach((cell, cellIndex) => {
        if (cell.data.code === option.code) {
          data[rowIndex].cellData[cellIndex].data = { ...option };
        }
      });
    });

    this.props.updateSessionsWithSummary({ summary: { ...summary, data } });
  };

  handleRefresh = () => {
    if (this.state.useCurrentDateTime) {
      this.handleChange("endDate", moment().toISOString(true), () =>
        this.loadSessions(),
      );
    } else {
      this.loadSessions();
    }
  };

  loadSessions = () => {
    const searchParams = this.state.searchParams;
    const { operator, company, subcompany, site } =
      this.props.filters.filterSelected;
    const { filterSelected, filterData } = this.props.filters;

    this.setState({ loading: true }, () => {
      this.props
        .fetchSessionsWithSummary(
          searchParams.startDate,
          searchParams.endDate,
          operator,
          company.id,
          subcompany.id,
          site.id,
          searchParams.sessionId,
          searchParams.assetNo,
          searchParams.playerId,
          searchParams.transactionRef,
          searchParams.zeroBetSessions,
          prepareFilter(filterSelected.regions, filterData.regions),
          prepareFilter(filterSelected.manufacturer, filterData.manufacturer),
          prepareFilter(filterSelected.game, filterData.game),
          prepareFilter(filterSelected.machinetypes, filterData.machinetypes),
          this.getDetails(),
          this.state.searchParams.pageNumber,
        )
        .then(() => this.setState({ loading: false }))
        .catch(() => this.setState({ loading: false }));
    });
  };

  loadExport = () => {
    const searchParams = this.state.searchParams;
    const { operator, company, subcompany, site } =
      this.props.filters.filterSelected;
    const { filterSelected, filterData } = this.props.filters;

    fetchSessionsExport(
      searchParams.startDate,
      searchParams.endDate,
      operator,
      company.id,
      subcompany.id,
      site.id,
      searchParams.sessionId,
      searchParams.assetNo,
      searchParams.playerId,
      searchParams.transactionRef,
      searchParams.zeroBetSessions,
      prepareFilter(filterSelected.regions, filterData.regions),
      prepareFilter(filterSelected.manufacturer, filterData.manufacturer),
      prepareFilter(filterSelected.game, filterData.game),
      prepareFilter(filterSelected.machinetypes, filterData.machinetypes),
      this.getDetails(),
    );
  };

  render() {
    const showSummary =
      this.props.filters.filterSelected.operator && this.state.showSummary;
    const { pageNumber } = this.state.searchParams;
    const { pageCount } = this.props.sessionsByCompany;
    const { showOrgTree } = this.props.filters;

    return (
      <React.Fragment>
        <Grid className="h-100 gs-flex-container-vertical mt-0">
          <Grid.Row>
            <Grid.Column>
              <div className="flex-vertical">
                <div className="flex-shrink" style={{ width: "39px" }}>
                  <OrgTreeButton
                    onClick={() =>
                      this.props.fetchSessionsFilters({
                        showOrgTree: !showOrgTree,
                      })
                    }
                    active={showOrgTree}
                  />
                  <SummaryButton
                    size="medium"
                    disabled={!this.props.filters.filterSelected.operator}
                    onClick={() =>
                      this.setState({ showSummary: !this.state.showSummary })
                    }
                    active={!this.state.showSummary}
                  />
                  <DownloadButton
                    disabled={!this.props.filters.hasSelection}
                    onClick={this.loadExport}
                    size="medium"
                  />
                </div>
                <div className="flex-stretch ml-1">
                  <CompanyFilters
                    hasSelection={this.props.filters.hasSelection}
                    {...this.state.searchParams}
                    company={this.props.filters.filterSelected.company}
                    subcompany={this.props.filters.filterSelected.subcompany}
                    site={this.props.filters.filterSelected.site}
                    useCurrentDateTime={this.state.useCurrentDateTime}
                    setUseCurrentDateTime={this.setUseCurrentDateTime}
                    onChange={this.handleChange}
                    refresh={this.handleRefresh}
                    refreshDisabled={
                      !this.props.filters.hasSelection || this.state.loading
                    }
                    loading={this.state.loading}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="flex-stretch">
            {showSummary && (
              <Grid.Column
                computer="6"
                largeScreen="4"
                widescreen="3"
                className="gs-flex-container-vertical"
                style={{
                  overflow: "auto",
                  maxHeight: `${this.state.windowHeight < 901 ? 65 : this.state.windowHeight < 1000 ? 68 : 70}vh`,
                }}
              >
                <DataGrid
                  loading={this.state.loading}
                  headings={this.props.sessionsByCompany.summary.headerDef}
                  data={this.props.sessionsByCompany.summary.data}
                  rowDef={this.props.sessionsByCompany.summary.rowDef}
                  hideOverflow
                  disableSort={true}
                  disableActiveRow={true}
                  onOptionChange={this.handleOptionChange}
                />
              </Grid.Column>
            )}
            <Grid.Column
              computer={showSummary ? "10" : "16"}
              largeScreen={showSummary ? "12" : "16"}
              widescreen={showSummary ? "13" : "16"}
              className="gs-flex-container-vertical"
            >
              <DataGrid
                loading={this.state.loading}
                headings={this.props.sessionsByCompany.grid.headerDef}
                rowDef={this.props.sessionsByCompany.grid.rowDef}
                data={this.props.sessionsByCompany.grid.data}
                onRowClick={this.handleSessionSelection}
                fullHeight
              />
              {pageCount > 1 && (
                <div className="flex-shrink mt-1">
                  <Pagination
                    totalPages={pageCount}
                    size="mini"
                    activePage={pageNumber}
                    onPageChange={(event, { activePage }) =>
                      this.setState(
                        {
                          searchParams: {
                            ...this.state.searchParams,
                            pageNumber: activePage,
                          },
                        },
                        () => this.handleRefresh(),
                      )
                    }
                  />
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {this.state.sessionId && (
          <SessionDetailsModal
            modalOpen={this.state.modalOpen}
            handleClose={this.handleClose}
            sessionId={this.state.sessionId}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    filters: state.sessionsFilters,
    sessionsByCompany: state.sessionsByCompany,
    searchParams: state.searchParams,
  };
};

export default connect(mapStateToProps, {
  fetchSessionsWithSummary,
  updateSessionsWithSummary,
  fetchSession,
  fetchBlankSummarySessions,
  fetchSessionsFilters,
  emptySessionsSection,
})(SessionsView);
